
<template>
  <div>
    <!-- b-table -->
    <b-skeleton-table
      v-if="is_loading"
      :rows="9"
      :columns="10"
      :table-props="{ bordered: true, striped: true }"
    ></b-skeleton-table>
    <div>
      <div class="d-flex">
        <GenericButtonDownload
          v-if="!is_loading && grade_report"
          :tooltip_text="$t('toolbar.reports.reportXlsx')"
          :click_button="DownloadExcel"
          :class="`ml-auto mr-0`"
          :disabled_button="disabled_button_excel"
          :icon_excel="true"
        ></GenericButtonDownload>
      </div>
    </div>
    <b-table-simple
      bordered
      small
      class="table-wrapper"
      responsive
      v-if="!is_loading && grade_report"
    >
      <b-thead>
        <b-tr>
          <b-th
            class="text-center align-middle primary-header"
            :colspan="6 + grade_report.matters.length"
          >
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-center mx-auto">
                {{
                  $getVisibleNames(
                    "manual.reporte.individual_grades",
                    false,
                    $t("toolbar.reports.individualGradesBy")
                  ).toUpperCase()
                }}
                {{
                  $getVisibleNames(
                    "mesh.egressprofilematter",
                    false,
                    "Asignatura"
                  ).toUpperCase()
                }}
                {{ $t("toolbar.reports.of").toUpperCase() }}
                {{
                  $getVisibleNames(
                    "mesh.egressprofile",
                    false,
                    "Perfil de Egreso"
                  ).toUpperCase()
                }}
              </span>
            </div>
          </b-th>
        </b-tr>
        <b-tr>
          <b-th
            class="text-center align-middle secondary-header"
            colspan="6"
          ></b-th>
          <template v-for="level in grade_report.plan_levels">
            <b-th
              :key="`plan_level-${level.plan_level}`"
              v-if="level.matters.length > 0"
              class="text-center align-middle secondary-header"
              :colspan="level.matters.length"
            >
              {{ grade_report.egress_profile?.regime_name.toUpperCase() }}
              {{ level.plan_level }}
            </b-th>
          </template>
        </b-tr>
        <b-tr>
          <b-th class="text-center align-middle secondary-header">{{
            $t("toolbar.reports.number")
          }}</b-th>
          <b-th class="text-center align-middle secondary-header">{{
            $t("toolbar.reports.lastName").toUpperCase()
          }}</b-th>
          <b-th class="text-center align-middle secondary-header">{{
            $t("toolbar.reports.firstName").toUpperCase()
          }}</b-th>
          <b-th class="text-center align-middle secondary-header">{{
            $t("reusable.common.rut").toUpperCase()
          }}</b-th>
          <b-th class="text-center align-middle secondary-header">{{
            $t("toolbar.reports.cohort").toUpperCase()
          }}</b-th>
          <b-th class="text-center align-middle secondary-header">{{
            $t("toolbar.reports.regime").toUpperCase()
          }}</b-th>
          <template v-for="level in grade_report.plan_levels">
            <template v-for="matter in level.matters">
              <b-th
                class="text-center align-middle secondary-header"
                :key="`matter-${matter.id}`"
              >
                {{ matter.name }}</b-th
              >
            </template>
          </template>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="(evaluatee, index) in grade_report.evaluatees"
          :key="`evaluatee-${evaluatee.id}`"
        >
          <b-td>{{ index + 1 }}</b-td>
          <b-td class="text-left">{{ evaluatee.last_name }}</b-td>
          <b-td class="text-left">{{ evaluatee.first_name }}</b-td>
          <b-td>{{ evaluatee.identifier }}</b-td>
          <b-td>
            <span
              v-b-tooltip.hover="
                `${$t('toolbar.reports.declaredCohort')}: ${evaluatee.declared_cohort}`
              "
              >{{ evaluatee.cohort }}</span
            >
          </b-td>
          <b-td>
            <RegimeExplanationBadge
              :evaluatee_id="evaluatee.id"
              :is_on_track="evaluatee.is_on_track"
              :label="regimeLabel(evaluatee.is_on_track)"
            ></RegimeExplanationBadge>
          </b-td>
          <template v-for="level in grade_report.plan_levels">
            <template
              v-for="evaluatee_plan_level in evaluatee.plan_levels.filter(
                (x) => x.plan_level == level.plan_level
              )"
            >
              <template
                v-for="evaluatee_matter_grade in evaluatee_plan_level.matters_grades"
              >
                <b-td
                  :key="`evaluatee-${evaluatee.id}-level-${level.plan_level}-plan_level-${evaluatee_plan_level.plan_level}-matter-${evaluatee_matter_grade.matter_id}`"
                  :style="`${
                    evaluatee_matter_grade.historical_grades.length > 0
                      ? 'border: 1px solid black !important;'
                      : ''
                  }background-color:${
                    evaluatee_matter_grade.grade_color
                  } !important;`"
                  :id="`popover-evaluatee-${evaluatee.id}-level-${level.plan_level}-plan_level-${evaluatee_plan_level.plan_level}-matter-${evaluatee_matter_grade.matter_id}`"
                  :class="{
                    'popover-icon':
                      evaluatee_matter_grade.historical_grades.length > 0,
                  }"
                >
                  <div>
                    <span v-if="evaluatee_matter_grade.current_grade == '-'">
                      -
                    </span>
                    <span v-else>
                      {{ evaluatee_matter_grade.current_grade | Round }}
                    </span>
                    <b-popover
                      v-if="evaluatee_matter_grade.historical_grades.length > 0"
                      :target="`popover-evaluatee-${evaluatee.id}-level-${level.plan_level}-plan_level-${evaluatee_plan_level.plan_level}-matter-${evaluatee_matter_grade.matter_id}`"
                      triggers="hover"
                      placement="top"
                      boundary="viewport"
                    >
                      <template #title>
                        {{ $t("toolbar.reports.gradeHistoryIn") }}
                        {{ evaluatee_matter_grade.matter_name }}
                      </template>
                      <template>
                        <div class="visible_columns">
                          <div
                            v-for="student_result in evaluatee_matter_grade.historical_grades"
                            :key="`b-popover-body-${level.plan_level}-${student_result.period_name}-${student_result.is_current}`"
                          >
                            {{ student_result.period_name }}:
                            {{ student_result.grade }}
                            {{
                              student_result.is_current
                                ? `(${$t(
                                    "toolbar.reports.current"
                                  ).toUpperCase()})`
                                : ""
                            }}
                          </div>
                        </div>
                      </template>
                    </b-popover>
                  </div>
                </b-td>
              </template>
            </template>
          </template>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <b-table-simple
      bordered
      small
      responsive
      v-if="grade_report && grade_report.grade_ranges"
      class="table-assesment-range-table"
    >
      <b-thead>
        <b-tr>
          <b-th class="primary-header" colspan="2"
            >{{ $t("toolbar.reports.gradeRanges").toUpperCase() }}
          </b-th>
        </b-tr>
        <b-tr>
          <b-th class="secondary-header">{{
            $t("toolbar.reports.name").toUpperCase()
          }}</b-th>
          <b-th class="secondary-header range-column">{{
            $t("toolbar.reports.rangePercentage").toUpperCase()
          }}</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="grade_range in grade_report.grade_ranges"
          :key="`row-${grade_range.id}`"
        >
          <b-td class="text-center aling-middle">{{ grade_range.name }}</b-td>
          <b-td
            class="text-center aling-middle"
            :style="`background-color:${grade_range.color} !important`"
          >
            <div>
              {{ grade_range.start_grade }}
              <span v-if="grade_range.include_start_grade">&le;</span>
              <span v-else>&lt;</span>
              x
              <span v-if="grade_range.include_finish_grade">&le;</span>
              <span v-else>&lt;</span>
              {{ grade_range.finish_grade }}
            </div>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <!-- Grafico -->
    <GenericGraphic
      v-if="matterOptions.length > 0"
      :labels="chartLabels"
      :datasets="chartDatasets"
      :legend_labels="legendLabels"
      :title_graphic="`${$t(
        'toolbar.reports.individualGradesBy'
      ).toUpperCase()} ${$getVisibleNames(
        'mesh.egressprofilematter',
        false,
        'Asignatura'
      ).toUpperCase()} ${$t(
        'toolbar.reports.of'
      ).toUpperCase()} ${$getVisibleNames(
        'mesh.egressprofile',
        false,
        'Perfil de Egreso'
      ).toUpperCase()} ${$t(
        'toolbar.reports.byLevelsAndRanges'
      ).toUpperCase()}`"
      :total_students="totalStudents"
      :label_y_axis="`${$t('toolbar.reports.numberOfStudents')}`"
      :title_table_circular="`${$t('toolbar.reports.level')}`"
      :selected_assessment="selected_matter"
    >
      <template #selector>
        <div class="d-flex">
          <b-form-select
            v-model="selected_matter"
            :options="matterOptions"
            @change="updateMatter"
            class="select-level"
          ></b-form-select>
        </div>
      </template>
    </GenericGraphic>
  </div>
</template>

<script>
// import * as names from "@/store/names";
// import { mapGetters } from "vuex";
import {
  showLoadingAlert,
  downloadFileFromResponse,
  EventBus,
} from "@/utils/utils";

export default {
  name: "IndividualMatterGradesReportTable",
  components: {
    GenericButtonDownload: () =>
      import("@/components/reusable/Buttons/GenericButtonDownload"),
    GenericGraphic: () => import("@/components/reusable/GenericGraphic"),
    RegimeExplanationBadge: () =>
      import("@/components/reports/TeachingReport/RegimeExplanationBadge"),
  },
  props: {
    egress_profile: {
      type: Object,
      required: true,
    },
    cohorts_: {
      type: Array,
      required: true,
    },
    campuses_: {
      type: Array,
    },
    grade_range_group: {
      type: [Number, null],
      required: false,
      default: null,
    },
    student_status_: {
      type: Array,
      default: () => [],
    },
    cohort_indices_: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      is_loading: false,
      disabled_button_excel: false,
      grade_report: null,
      selected_matter: "compare_all",
    };
  },
  computed: {
    isOnTrack() {
      const student_status_ids = this.student_status_.map((x) => x.id);
      if (student_status_ids.includes(1) && student_status_ids.includes(2))
        return null;
      else if (student_status_ids.includes(1)) return true;
      else if (student_status_ids.includes(2)) return false;
      return null;
    },
    matterOptions() {
      if (!this.grade_report?.plan_levels) return [];
      const options = [];
      options.push({
        value: "compare_all",
        text: this.$t("toolbar.reports.viewAllLevels"),
      });
      this.grade_report.plan_levels.forEach((level) => {
        options.push({
          value: level.plan_level,
          text: `${
            this.grade_report.egress_profile?.regime_name ||
            this.$t("toolbar.reports.level")
          } ${level.plan_level}`,
        });
      });

      return options;
    },
    totalStudents() {
      return this.grade_report?.evaluatees?.length || 0;
    },
    chartLabels() {
      if (!this.grade_report?.plan_levels) {
        return [];
      }
      // Para la opción Grupal - mostrar todos los niveles
      if (this.selected_matter == "compare_all") {
        return this.grade_report.plan_levels.map(
          (level) =>
            `${this.grade_report.egress_profile?.regime_name || ""} ${
              level.plan_level
            }`
        );
      }
      // Para la opción individual - mostrar las asignaturas del nivel seleccionado
      const level = this.grade_report.plan_levels.find(
        (x) => x.plan_level == this.selected_matter
      );

      if (!level) return [];
      return level.matters.map((x) => x.name);
    },
    chartDatasets() {
      if (
        !this.grade_report?.grade_ranges ||
        !this.grade_report?.evaluatees ||
        !this.selected_matter
      ) {
        return [];
      }

      const datasets = [];

      // Si se selecciona "Comparar todos" - mostrar por niveles
      if (this.selected_matter == "compare_all") {
        // Crear un dataset por cada grade_ranges
        this.grade_report.grade_ranges.forEach((range) => {
          const data = this.grade_report.plan_levels.map((level) => {
            let count = 0;

            this.grade_report.evaluatees.forEach((student) => {
              const studentLevel = student.plan_levels.find(
                (x) => x.plan_level == level.plan_level
              );

              if (studentLevel) {
                // Contar cada nota individual de cada asignatura
                studentLevel.matters_grades.forEach((matterGrade) => {
                  if (
                    matterGrade.current_grade !== null &&
                    matterGrade.current_grade !== "-"
                  ) {
                    const grade = matterGrade.current_grade;

                    const inRange =
                      (range.include_start_grade
                        ? grade >= range.start_grade
                        : grade > range.start_grade) &&
                      (range.include_finish_grade
                        ? grade <= range.finish_grade
                        : grade < range.finish_grade);

                    if (inRange) {
                      count++;
                    }
                  }
                });
              }
            });

            return count;
          });

          datasets.push({
            label: range.name,
            data: data,
            backgroundColor: range.color,
          });
        });
      } else {
        // Si se selecciona un nivel específico - mostrar por asignaturas
        const level = this.grade_report.plan_levels.find(
          (x) => x.plan_level == this.selected_matter
        );

        if (!level) return [];

        // Crear un dataset por cada grade_ranges según las asignaturas del nivel
        this.grade_report.grade_ranges.forEach((range) => {
          const data = level.matters.map((matter) => {
            let count = 0;

            this.grade_report.evaluatees.forEach((student) => {
              const studentLevel = student.plan_levels.find(
                (x) => x.plan_level == level.plan_level
              );

              if (studentLevel) {
                const matterGrade = studentLevel.matters_grades.find(
                  (x) => x.matter_id == matter.id
                );

                if (matterGrade && matterGrade.current_grade !== null) {
                  const grade = matterGrade.current_grade;

                  const inRange =
                    (range.include_start_grade
                      ? grade >= range.start_grade
                      : grade > range.start_grade) &&
                    (range.include_finish_grade
                      ? grade <= range.finish_grade
                      : grade < range.finish_grade);

                  if (inRange) {
                    count++;
                  }
                }
              }
            });

            return count;
          });

          datasets.push({
            label: range.name,
            data: data,
            backgroundColor: range.color,
          });
        });
      }

      return datasets;
    },

    legendLabels() {
      if (this.grade_report?.grade_ranges) {
        return this.grade_report.grade_ranges.map((x) => ({
          label: x.name,
          color: x.color,
        }));
      }
      return [];
    },
  },
  methods: {
    updateMatter(selectedId) {
      this.selected_matter = selectedId;
    },
    regimeLabel(isOnTrack) {
      if (isOnTrack === true) return this.$t("toolbar.reports.inRegime");
      if (isOnTrack === false) return this.$t("toolbar.reports.notInRegime");
      return "-";
    },
    DownloadExcel() {
      this.disabled_button_excel = true;
      showLoadingAlert(
        this.$t("reusable.common.generatingExcel"),
        this.$t("reusable.common.pleaseWaitFile")
      );
      this.$restful
        .Post(
          `/evaluations2/excel_individual_matter_grades/`,
          {
            egress_profile: this.egress_profile.id,
            cohort_year_filter_ids: this.cohorts_.map((x) => x.cohort),
            cohort_index_filter_ids: this.cohort_indices_.map(
              (x) => x.cohort_index
            ),
            campus_filter_ids: this.campuses_.map((x) => x.id),
            grade_range_group: this.grade_range_group,
            is_on_track: this.isOnTrack,
          },
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          this.$swal.close();
          if (response) {
            downloadFileFromResponse(
              response,
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              () => {
                this.disabled_button_excel = false;
              },
              () => {
                this.disabled_button_excel = false;
                this.$swal.fire(
                  this.$t("reusable.common.error"),
                  this.$t("reusable.common.downloadError"),
                  "error"
                );
              }
            );
          } else {
            this.disabled_button_excel = false;
            this.$swal.fire(
              this.$t("reusable.common.error"),
              this.$t("reusable.common.generateError"),
              "error"
            );
          }
        })
        .catch(() => {
          this.disabled_button_excel = false;
          this.$swal.close();
          this.$swal.fire(
            this.$t("reusable.common.error"),
            this.$t("reusable.common.problemGenerating"),
            "error"
          );
        });
    },
    getGradeReport() {
      this.is_loading = true;
      this.$restful
        .Post(`/evaluations2/individual_matter_grades/`, {
          egress_profile: this.egress_profile.id,
          cohort_year_filter_ids: this.cohorts_.map((x) => x.cohort),
          cohort_index_filter_ids: this.cohort_indices_.map(
            (x) => x.cohort_index
          ),
          campus_filter_ids: this.campuses_.map((x) => x.id),
          grade_range_group: this.grade_range_group,
          is_on_track: this.isOnTrack,
        })
        .then((response) => {
          this.grade_report = response;
          this.$swal.close();
          EventBus.$emit("report_request_finished");
          this.is_loading = false;
        })
        .catch(() => {
          this.is_loading = false;
          this.$swal.close();
          EventBus.$emit("report_request_finished");
          this.$swal.fire(
            this.$t("reusable.common.error"),
            this.$t("toolbar.reports.problemGeneratingReport"),
            "error"
          );
        });
    },
    getMatterLabel(student_result, plan_level) {
      if (!this.grade_report) return "-";
      const level = this.grade_report.plan_levels.find(
        (x) => x.plan_level == plan_level
      );
      if (level) {
        const matter = level.matters.find((x) => x.id == student_result.id);
        if (matter) {
          const grade =
            student_result.grade == "-"
              ? "-"
              : this.$round(student_result.grade);
          return `${matter.name}: <strong>${grade}</strong>`;
        }
      }
      return "-";
    },
  },
  created() {
    this.getGradeReport();
  },
};
</script>

<style scoped>
.select-period {
  width: auto;
}
.primary-header {
  background-color: var(--kl-color-accent-medium) !important;
  color: white;
}
.secondary-header {
  background: var(--kl-table-header-bg) !important;
  color: var(--kl-table-header-text);
}
.visible_columns {
  max-height: 300px;
  overflow: auto;
}
.popover-icon {
  cursor: pointer;
}
.table-wrapper {
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: auto;
}

@media print {
  .table-wrapper {
    max-height: none;
    overflow: visible;
  }
}
</style>


